import type { SeedNetwork } from "@/lib/setup/preview";
import { directionLimits } from "@/lib/setup/recency";
import { fetchSeedNetwork, resolveS2Id } from "@/lib/trawl-utils";
import { S2UnavailableError } from "@/services/s2-client";
import { netKey, type SeedRef, useSetupStore } from "@/stores/setup-store";
import type { Recency } from "@/types/concept";

export type EnsuredSeed = { seed: SeedRef; network: SeedNetwork | null };

// netKey(seed key, recency) → the one fetch in flight for it. Module-level so
// Start joins a fetch the preview began instead of dropping that seed.
const inflight = new Map<string, Promise<SeedNetwork | null>>();

/** Pauses before re-asking S2 for a seed it didn't answer (each attempt
 *  already retries 429s internally, honoring Retry-After). */
let retryDelaysMs = [10_000, 30_000];

/** Test hooks. */
export function __resetInflight() { inflight.clear(); }
export function __setRetryDelays(ms: number[]) { retryDelaysMs = ms; }

/** The one path to a seed's network, for the preview at ③ and for Start.
 *  Resolves each seed to an S2 id (paper_id → DOI → title, via `resolveS2Id`),
 *  so imported .bib/Zotero seeds without an S2 id are trawled too, then fetches
 *  the network once per (seed, recency).
 *
 *  `network: null` = no S2 record or no network ("empty", final) or S2 never
 *  answered ("failed"). A network is all-or-nothing: a direction S2 didn't
 *  answer fails the seed (and retries) instead of caching a partial network.
 *
 *  retryFailed=false (Start): reuse what the preview settled — a seed the
 *  preview showed as failed stays out — so Start adds what the preview showed.
 *  Seeds the preview never settled (e.g. after a reload) are fetched. */
export function ensureNetworks(
  seeds: SeedRef[], recency: Recency | undefined,
  { retryFailed = true, onSettled }: { retryFailed?: boolean; onSettled?: () => void } = {},
): Promise<EnsuredSeed[]> {
  return Promise.all(seeds.map(async (seed) => {
    const network = await ensureOne(seed, recency, retryFailed);
    onSettled?.(); // ④'s "seed N of M" (ensureOne never rejects)
    return { seed, network };
  }));
}

function ensureOne(seed: SeedRef, recency: Recency | undefined, retryFailed: boolean): Promise<SeedNetwork | null> {
  const store = useSetupStore.getState();
  const s2 = store.resolved[seed.key];
  const cached = s2 ? store.networks[netKey(s2, recency)] : undefined;
  if (cached) return Promise.resolve(cached);

  const sk = netKey(seed.key, recency);
  const running = inflight.get(sk);
  if (running) return running;
  const settled = store.status[sk];
  if (settled === "empty" || (settled === "failed" && !retryFailed)) return Promise.resolve(null);

  store.setStatus(sk, "loading");
  // Deliberately not tied to the caller's lifetime: if the preview unmounts
  // mid-fetch (user hits Next), Start still gets the result.
  const signal = new AbortController().signal;
  const once = async (): Promise<SeedNetwork | null> => {
    const id = useSetupStore.getState().resolved[seed.key] ?? (await resolveS2Id(seed, signal, { strict: true }));
    if (!id) return null;
    useSetupStore.getState().setResolved(seed.key, id);
    const net = await fetchSeedNetwork(id, directionLimits(recency), signal, recency ?? "balanced");
    if (!net.citations.length && !net.references.length && !net.recommendations.length) return null;
    useSetupStore.getState().setNetwork(netKey(id, recency), net);
    return net;
  };
  const p = (async () => {
    for (let attempt = 0; ; attempt++) {
      try {
        const net = await once();
        useSetupStore.getState().setStatus(sk, net ? "ok" : "empty");
        return net;
      } catch (err) {
        if (!(err instanceof S2UnavailableError) || attempt >= retryDelaysMs.length) throw err;
        useSetupStore.getState().setStatus(sk, "retrying");
        await new Promise((r) => setTimeout(r, retryDelaysMs[attempt]));
      }
    }
  })()
    .catch(() => {
      useSetupStore.getState().setStatus(sk, "failed");
      return null;
    })
    .finally(() => inflight.delete(sk));
  inflight.set(sk, p);
  return p;
}
